import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { pageTitle } from '../../styles/pageStyles';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import PublicationLayout from '../../components/publication-layout/PublicationLayout';
import { fetchInvoiceDetail, InvoiceDetailResponse, InvoicePeriod } from './api';

// ── Styled ─────────────────────────────────────────────────────────────────

const Main = styled.div``;

const BackLink = styled.a`
  display: inline-block;
  font-size: 13px;
  color: #0094ab;
  text-decoration: none;
  margin-bottom: 16px;
  &:hover { text-decoration: underline; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  ${pageTitle}
  margin: 0 0 4px 0;
`;

const SubTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #777;
  margin: 0;
`;

const PeriodSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: #0094ab; }
`;

const DownloadWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
`;

const DownloadButton = styled.button`
  padding: 7px 14px;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const DownloadMenu = styled.ul<{ $open: boolean }>`
  display: ${p => p.$open ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  list-style: none;
  padding: 4px 0;
  margin: 4px 0 0 0;
  z-index: 10;
  min-width: 100px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);

  li a {
    display: block;
    padding: 8px 16px;
    font-size: 13px;
    color: #333;
    text-decoration: none;
    &:hover { background: #f5f5f5; }
  }
`;

const SummarySection = styled.div`
  max-width: 500px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
`;

const BoldRow = styled(SummaryRow)`font-weight: 700;`;

const IndentRow = styled(SummaryRow)`
  padding-left: 16px;
  font-size: 13px;
  color: #555;
`;

const SectionLabel = styled.h5`
  font-size: 14px;
  font-weight: 600;
  margin: 16px 0 4px 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Tooltip = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ccc;
  color: white;
  font-size: 10px;
  text-align: center;
  line-height: 14px;
  cursor: help;
`;

const TableWrapper = styled.div`
  margin-top: 32px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

function formatDollars(amount: number): string {
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

// ── Component ──────────────────────────────────────────────────────────────

function InvoiceDetailPage() {
  const { publicationId, invoiceId } = useParams<{ publicationId: string; invoiceId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<InvoiceDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);

  useDocumentTitle(data ? `${data.invoice.title} : Financial activity` : 'Financial activity');

  const load = useCallback(() => {
    if (!publicationId || !invoiceId) return;
    setError(null);
    fetchInvoiceDetail(publicationId, invoiceId).then(setData).catch(() => {
      setError('Unable to load invoice. Please refresh the page.');
    });
  }, [publicationId, invoiceId]);

  useEffect(() => { load(); }, [load]);

  if (!publicationId || !invoiceId) return null;

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    const parts = url.split('/');
    const newInvoiceId = parts[parts.length - 1];
    navigate(`/publications/${publicationId}/invoices/${newInvoiceId}`);
  };

  return (
    <PublicationLayout>
      <Main>
        <BackLink href={`/publications/${publicationId}/invoices`}>
          ‹ Back to overview
        </BackLink>

        <TitleRow>
          <TitleBlock>
            <PageTitle>Financial activity</PageTitle>
            {data && <SubTitle>{data.publication_name}</SubTitle>}
          </TitleBlock>

          {data && data.all_periods.length > 0 && (
            <PeriodSelect
              value={`/publications/${publicationId}/invoices/${invoiceId}`}
              onChange={handlePeriodChange}
            >
              {data.all_periods.map((p: InvoicePeriod) => (
                <option key={p.id} value={p.url}>{p.label}</option>
              ))}
            </PeriodSelect>
          )}
        </TitleRow>

        {data && (
          <>
            <DownloadWrapper>
              <DownloadButton onClick={() => setDownloadOpen(o => !o)}>
                Download ▾
              </DownloadButton>
              <DownloadMenu $open={downloadOpen}>
                <li>
                  <a
                    href={`/publications/${publicationId}/invoices/${invoiceId}.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setDownloadOpen(false)}
                  >
                    PDF
                  </a>
                </li>
                <li>
                  <a
                    href={`/publications/${publicationId}/invoices/${invoiceId}.csv`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setDownloadOpen(false)}
                  >
                    CSV
                  </a>
                </li>
              </DownloadMenu>
            </DownloadWrapper>

            <SummarySection>
              {data.invoice.purchase_order_number && (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                  PO #{data.invoice.purchase_order_number}
                </p>
              )}

              <BoldRow>
                <SectionLabel>
                  New Charges
                  <Tooltip title="Total charges for this period.">?</Tooltip>
                </SectionLabel>
                <span>{formatDollars(data.new_charges.total)}</span>
              </BoldRow>
              {data.new_charges.breakdown.map(item => (
                <IndentRow key={item.category}>
                  <span>{item.category}</span>
                  <span>{formatDollars(item.amount)}</span>
                </IndentRow>
              ))}

              <BoldRow>
                <SectionLabel>Credits</SectionLabel>
                <span>{formatDollars(data.credits)}</span>
              </BoldRow>

              {data.pending_payments !== undefined && (
                <BoldRow>
                  <SectionLabel>
                    Pending payments
                    <Tooltip title="Total upcoming payments on stories still in progress.">?</Tooltip>
                  </SectionLabel>
                  <span>{formatDollars(data.pending_payments)}</span>
                </BoldRow>
              )}
            </SummarySection>

            {data.transactions.length > 0 && (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Charge</Th>
                      <Th>Category</Th>
                      <Th>Campaigns</Th>
                      <Th>Story ID</Th>
                      <Th>Contributor</Th>
                      <Th>Date</Th>
                      <Th>Notes</Th>
                      <Th style={{ textAlign: 'right' }}>Amount</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t, i) => (
                      <tr key={i}>
                        <Td>
                          {t.story_url ? (
                            <a href={t.story_url} target="_blank" rel="noopener noreferrer">
                              {t.title ?? '—'}
                            </a>
                          ) : (
                            t.title ?? '—'
                          )}
                        </Td>
                        <Td>{t.category}</Td>
                        <Td>{t.campaigns ?? '—'}</Td>
                        <Td>{t.story_id ?? '—'}</Td>
                        <Td>{t.contributor ?? '—'}</Td>
                        <Td>{t.date ?? '—'}</Td>
                        <Td>{t.notes ?? '—'}</Td>
                        <Td style={{ textAlign: 'right', color: t.is_credit ? '#2e7d32' : 'inherit' }}>
                          {formatDollars(t.amount)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            )}
          </>
        )}

        {data === null && !error && <p>Loading...</p>}
        {error && <p style={{ color: '#c0392b', padding: '20px 0', fontSize: '14px' }}>{error}</p>}
      </Main>
    </PublicationLayout>
  );
}

export default InvoiceDetailPage;
